import { create } from '@bufbuild/protobuf';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { z } from 'zod';

import {
	type MissionSearchFilter,
	MissionSearchFilterSchema,
	MissionSort,
} from '@/gen/backoffice/v1/backoffice_pb';
import { BookingStatus } from '@/gen/booking/v1/booking_pb';
import { TransportType } from '@/gen/mobility/v1/common_pb';

export const STATUS_FILTERS = {
	pending: BookingStatus.PENDING,
	prebooked: BookingStatus.PREBOOKED,
	confirmed: BookingStatus.CONFIRMED,
	accepted: BookingStatus.ACCEPTED,
	canceled: BookingStatus.CANCELED,
	error: BookingStatus.ERROR,
} as const;
export type StatusFilter = keyof typeof STATUS_FILTERS;

export const TRANSPORT_FILTERS = {
	train: TransportType.TRAIN,
	taxi: TransportType.TAXI,
	rental_vehicle: TransportType.RENTAL_VEHICLE,
} as const;
export type TransportFilter = keyof typeof TRANSPORT_FILTERS;

const isoDay = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/)
	.optional()
	.catch(undefined);
const text = z.string().trim().min(1).optional().catch(undefined);

// Criteria and filters live in the URL so a view can be shared.
export const missionSearchParamsSchema = z.object({
	q: text,
	firstName: text,
	lastName: text,
	caseNumber: text,
	missionId: text,
	plate: text,
	phone: text,
	createdFrom: isoDay,
	createdTo: isoDay,
	statuses: z
		.array(z.enum(Object.keys(STATUS_FILTERS) as [StatusFilter]))
		.optional()
		.catch(undefined),
	transports: z
		.array(z.enum(Object.keys(TRANSPORT_FILTERS) as [TransportFilter]))
		.optional()
		.catch(undefined),
	provider: text,
	sort: z.enum(['created', 'departure']).optional().catch(undefined),
});

export type MissionSearchParams = z.infer<typeof missionSearchParamsSchema>;

const parseDay = (value: string) => {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const toSearchFilter = (
	params: MissionSearchParams,
): MissionSearchFilter => {
	let createdBefore: Date | undefined;
	if (params.createdTo) {
		createdBefore = parseDay(params.createdTo);
		createdBefore.setDate(createdBefore.getDate() + 1);
	}
	return create(MissionSearchFilterSchema, {
		query: params.q,
		beneficiaryFirstName: params.firstName,
		beneficiaryLastName: params.lastName,
		caseNumber: params.caseNumber,
		missionId: params.missionId,
		vehiclePlate: params.plate,
		phone: params.phone,
		createdAfter: params.createdFrom
			? timestampFromDate(parseDay(params.createdFrom))
			: undefined,
		createdBefore: createdBefore ? timestampFromDate(createdBefore) : undefined,
		itemStatuses: (params.statuses ?? []).map((s) => STATUS_FILTERS[s]),
		transportTypes: (params.transports ?? []).map((t) => TRANSPORT_FILTERS[t]),
		provider: params.provider,
	});
};

export const toSort = (params: MissionSearchParams): MissionSort =>
	params.sort === 'departure'
		? MissionSort.FIRST_DEPARTURE_DESC
		: MissionSort.CREATED_AT_DESC;

export const hasCriteria = (params: MissionSearchParams) =>
	Object.entries(params).some(
		([key, value]) =>
			key !== 'sort' &&
			value !== undefined &&
			(!Array.isArray(value) || value.length > 0),
	);
